import { useEffect, useState, type RefObject } from 'react';

interface Line {
  d: string;
  from: [number, number];
  to: [number, number];
}

/**
 * The thin accent line from the ringed comment to the card that shows what
 * the API returned for it. Measured after mount, so the server renders no line
 * and the client draws it once the page has laid out; it redraws on resize.
 */
export const Connector = ({
  stage,
  from,
  to,
  moves,
}: {
  stage: RefObject<HTMLElement | null>;
  from: RefObject<HTMLElement | null>;
  to: RefObject<HTMLElement | null>;
  /** changes whenever the drawn page moved under the ringed item */
  moves: number;
}) => {
  const [line, setLine] = useState<Line | null>(null);
  useEffect(() => {
    const box = stage.current;
    if (!box) return;
    const draw = () => {
      const s = box.getBoundingClientRect();
      const a = from.current?.getBoundingClientRect();
      const b = to.current?.getBoundingClientRect();
      if (!a || !b || a.width === 0 || b.width === 0) return setLine(null);
      const x0 = a.left - s.left;
      const y0 = a.top - s.top + a.height / 2;
      const x1 = b.right - s.left - 40;
      const y1 = b.top - s.top;
      setLine({
        d: `M ${x0} ${y0} C ${x0 - 60} ${y0}, ${x1} ${y1 - 50}, ${x1} ${y1}`,
        from: [x0, y0],
        to: [x1, y1],
      });
    };
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(box);
    return () => observer.disconnect();
  }, [stage, from, to, moves]);
  if (!line) return null;
  return (
    <svg aria-hidden="true" className="pointer-events-none absolute inset-0 size-full overflow-visible max-lg:hidden">
      <path d={line.d} fill="none" className="stroke-bg" strokeWidth={4} strokeLinecap="round" />
      <path d={line.d} fill="none" className="stroke-accent" strokeWidth={1.5} strokeLinecap="round" />
      <circle cx={line.from[0]} cy={line.from[1]} r={3.5} className="fill-accent stroke-bg" strokeWidth={1.5} />
      <circle cx={line.to[0]} cy={line.to[1]} r={3.5} className="fill-accent stroke-bg" strokeWidth={1.5} />
    </svg>
  );
};
